import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid,
  Avatar,
  Chip
} from '@mui/material';
import { fetchSensorPlacement } from '../api/sensorApi';

// A simple visualization of tunnel and sensor placement
const TunnelSensorMap = ({ 
  tunnelId, 
  highlightSection = null, 
  highlightSensor = null, 
  selectedSensors = [],
  // Add callback for toggling sensor selection
  onSensorToggle = null
}) => {
  const [placementData, setPlacementData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Load placement data for the tunnel
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('TunnelSensorMap: Loading data for tunnel:', tunnelId);
        const allPlacements = await fetchSensorPlacement();
        
        console.log('TunnelSensorMap: All placements:', allPlacements);
        
        // Filter to just this tunnel
        const tunnelPlacements = allPlacements.filter(p => p.tunnel === tunnelId);
        
        console.log('TunnelSensorMap: Filtered placements for tunnel', tunnelId, ':', tunnelPlacements.length);
        
        // Sort by placement (S1, S2, etc.)
        tunnelPlacements.sort((a, b) => {
          const aNum = parseInt(a.placement.substring(1));
          const bNum = parseInt(b.placement.substring(1));
          return aNum - bNum;
        });
        
        setPlacementData(tunnelPlacements);
      } catch (error) {
        console.error('Error loading placement data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (tunnelId) {
      loadData();
    } else {
      console.log('TunnelSensorMap: No tunnel selected');
      setPlacementData([]);
      setLoading(false);
    }
  }, [tunnelId]);
  
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading sensor map...
        </Typography>
      </Box>
    );
  }
  
  if (!tunnelId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No tunnel selected
        </Typography>
      </Box>
    );
  }
  
  if (placementData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No placement data available for tunnel: {tunnelId}
        </Typography>
      </Box>
    );
  }
  
  // Position-based colors
  const positionColors = {
    low: '#4caf50',    // green
    middle: '#2196f3', // blue
    roof: '#f44336'    // red
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Tunnel Sensor Map - {tunnelId}
      </Typography>
      
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mr: 2 }}>
            Legend:
          </Typography>
          {Object.entries(positionColors).map(([position, color]) => (
            <Chip
              key={position}
              label={position.charAt(0).toUpperCase() + position.slice(1)}
              avatar={<Avatar sx={{ bgcolor: color }}>{position[0].toUpperCase()}</Avatar>}
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
            />
          ))}
        </Box>
        
        {/* Add instruction text for clicking sensors */}
        {onSensorToggle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            Click on sensor numbers to toggle them in the chart
          </Typography>
        )}
      </Box>
      
      {/* Tunnel visualization */}
      <Box sx={{ 
        border: '1px solid #ccc', 
        borderRadius: 2, 
        p: 2, 
        backgroundColor: '#f5f5f5',
        position: 'relative',
        minHeight: 250
      }}>
        {/* Tunnel outline */}
        <Box sx={{ 
          height: '100px', 
          borderRadius: '100px 100px 0 0',
          backgroundColor: '#e0e0e0',
          border: '1px solid #aaa',
          position: 'relative',
          mb: 2,
          mx: 4
        }}>
          {/* Road */}
          <Box sx={{ 
            position: 'absolute',
            bottom: 0,
            left: '10%',
            right: '10%',
            height: '40px',
            backgroundColor: '#bdbdbd'
          }}/>
          
          {/* Placements */}
          <Grid container spacing={0} sx={{ position: 'absolute', bottom: '-20px', width: '100%' }}>
            {placementData.map((placement, index) => (
              <Grid 
                item 
                xs={Math.floor(12 / placementData.length)} 
                key={placement.placement}
                sx={{ 
                  textAlign: 'center',
                  // Highlight the section if selected
                  backgroundColor: highlightSection === placement.placement ? 'rgba(255, 235, 59, 0.2)' : 'transparent',
                  borderRadius: 1,
                  padding: 1,
                  border: highlightSection === placement.placement ? '1px dashed #ffc107' : 'none'
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                  {placement.placement}
                </Typography>
                
                {/* Sensors at this placement */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  {/* Roof */}
                  <Box sx={{ mb: 1 }}>
                    {placement.sensors.roof.map(sensor => (
                      <Avatar 
                        key={sensor}
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          fontSize: '0.75rem',
                          backgroundColor: positionColors.roof,
                          transform: highlightSensor === sensor || selectedSensors.includes(sensor) ? 'scale(1.3)' : 'none',
                          transition: 'transform 0.3s ease',
                          boxShadow: highlightSensor === sensor 
                            ? '0 0 5px 2px rgba(244, 67, 54, 0.7)' 
                            : selectedSensors.includes(sensor) 
                              ? '0 0 5px 2px rgba(33, 150, 243, 0.5)'
                              : 'none',
                          opacity: selectedSensors.length > 0 && !selectedSensors.includes(sensor) ? 0.5 : 1,
                          cursor: onSensorToggle ? 'pointer' : 'default',
                          '&:hover': {
                            transform: 'scale(1.3)',
                            boxShadow: '0 0 5px 2px rgba(0, 0, 0, 0.2)'
                          }
                        }}
                        title={`${sensor} (Roof) - Click to toggle in chart`}
                        onClick={() => {
                          // Call the toggle callback if provided
                          if (onSensorToggle) {
                            onSensorToggle(sensor);
                          }
                        }}
                      >
                        {sensor.slice(-2)}
                      </Avatar>
                    ))}
                  </Box>
                  
                  {/* Middle */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {placement.sensors.middle.map(sensor => (
                      <Avatar 
                        key={sensor}
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          fontSize: '0.75rem',
                          backgroundColor: positionColors.middle,
                          transform: highlightSensor === sensor || selectedSensors.includes(sensor) ? 'scale(1.3)' : 'none',
                          transition: 'transform 0.3s ease',
                          boxShadow: highlightSensor === sensor 
                            ? '0 0 5px 2px rgba(33, 150, 243, 0.7)' 
                            : selectedSensors.includes(sensor) 
                              ? '0 0 5px 2px rgba(33, 150, 243, 0.5)'
                              : 'none',
                          opacity: selectedSensors.length > 0 && !selectedSensors.includes(sensor) ? 0.5 : 1,
                          cursor: onSensorToggle ? 'pointer' : 'default',
                          '&:hover': {
                            transform: 'scale(1.3)',
                            boxShadow: '0 0 5px 2px rgba(0, 0, 0, 0.2)'
                          }
                        }}
                        title={`${sensor} (Middle) - Click to toggle in chart`}
                        onClick={() => {
                          // Call the toggle callback if provided
                          if (onSensorToggle) {
                            onSensorToggle(sensor);
                          }
                        }}
                      >
                        {sensor.slice(-2)}
                      </Avatar>
                    ))}
                  </Box>
                  
                  {/* Low */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {placement.sensors.low.map(sensor => (
                      <Avatar 
                        key={sensor}
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          fontSize: '0.75rem',
                          backgroundColor: positionColors.low,
                          transform: highlightSensor === sensor || selectedSensors.includes(sensor) ? 'scale(1.3)' : 'none',
                          transition: 'transform 0.3s ease',
                          boxShadow: highlightSensor === sensor 
                            ? '0 0 5px 2px rgba(76, 175, 80, 0.7)' 
                            : selectedSensors.includes(sensor) 
                              ? '0 0 5px 2px rgba(33, 150, 243, 0.5)'
                              : 'none',
                          opacity: selectedSensors.length > 0 && !selectedSensors.includes(sensor) ? 0.5 : 1,
                          cursor: onSensorToggle ? 'pointer' : 'default',
                          '&:hover': {
                            transform: 'scale(1.3)',
                            boxShadow: '0 0 5px 2px rgba(0, 0, 0, 0.2)'
                          }
                        }}
                        title={`${sensor} (Low) - Click to toggle in chart`}
                        onClick={() => {
                          // Call the toggle callback if provided
                          if (onSensorToggle) {
                            onSensorToggle(sensor);
                          }
                        }}
                      >
                        {sensor.slice(-2)}
                      </Avatar>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

export default TunnelSensorMap;